import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Focus on navigation (WCAG 2.4.3, 2026-10-21). A link that leads to another page is unmounted
// with the page it was on, so focus fell to <body>: the next Tab started at the top of the
// document and a screen reader stayed silent about the new page. After every change of the path
// (not the first page, not a change of the query string such as a table's filters) focus moves
// to the new page's main heading (<main> h1; boku-ui's Layout gives it tabIndex -1 for this), or
// to <main> when the page has no heading yet.
//
// Focus is moved only when it was lost: on <body>, on an element no longer in the document, or
// in the top bar (its nav links stay mounted, and on a phone the menu closes over them). Focus
// that is still inside the page — the dataset tabs moved with the arrow keys, Management's
// section links — stays where the person put it.
//
// Focus can be lost a while after the path changed: a page that stays mounted (a hosted table
// whose key link leads to another table) keeps the focused link until the new rows arrive. So
// for WATCH_MS after the change it looks every TICK_MS: focus lost -> the heading (or <main>,
// once one is there); focus moved by the person to something else -> it stops.
//
// An element this file makes focusable itself (it had no tabindex) also gets outline: none, as
// boku-ui's Layout h1 has. Otherwise Chrome matches :focus-visible on the programmatic focus of a
// freshly loaded page, and boku-ui's global rule drew a black ring around the heading: on a
// signed-out visit, the redirect to /login ringed <h1>Unio</h1> until the next click (2026-10-30).
// It is not a control, so no ring is lost that keyboard users need; Tab goes on to the form.

const WATCH_MS = 4000;
const TICK_MS = 50;

function focusLost() {
  const el = document.activeElement;
  if (!el || el === document.body || el === document.documentElement || !el.isConnected) return true;
  return !!el.closest("header, [data-topbar]");
}

function target() {
  const main = document.querySelector("main");
  if (!main) return null;
  const h1 = main.querySelector("h1");
  const el = h1 || main;
  if (!el.hasAttribute("tabindex")) {
    el.setAttribute("tabindex", "-1");
    el.style.outline = "none";
  }
  return el;
}

export default function RouteFocus() {
  const { pathname } = useLocation();
  const previous = useRef(pathname);

  useEffect(() => {
    if (previous.current === pathname) return undefined;
    previous.current = pathname;
    const origin = document.activeElement;
    const until = Date.now() + WATCH_MS;
    let id = null;
    const tick = () => {
      id = null;
      if (focusLost()) {
        const el = target();
        if (el) { el.focus(); return; }
      } else if (document.activeElement !== origin) {
        return;                                // the person (or the page) put focus somewhere
      }
      if (Date.now() < until) id = setTimeout(tick, TICK_MS);
    };
    id = setTimeout(tick, 0);
    return () => { if (id !== null) clearTimeout(id); };
  }, [pathname]);

  return null;
}
